import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { SUPPORTED_CHAINS_BY_CHAIN_ID } from '../../constants/chain';
import { enforceRFQRules } from '../../utils/liquiditySourcesValidation';

/**
 * A data structure containing details for a single liquidity source.
 */
export type Source = {
  /** The name of the liquidity source rendered to the user. */
  displayName: string;
  /** Whether the source is included in the order. */
  enabled: boolean;
};

/**
 * A data structure containing indivdual Source objects keyed by their ID.
 */
export type SourcesList = {
  /** A Source object. */
  [key: string]: Source;
};

/**
 * A data structure containing Sources + metadata.
 * @remarks
 *
 * The `selected` and `total` properties have been denormalized in order to keep
 * read operations in O(1) time.
 */
type ChainSources = {
  /** The number of currently selected liquidity sources on the current chain. */
  selected: number;
  /** A SourcesList object. */
  sources: SourcesList;
  /** The total number of sources available on the current chain. */
  total: number;
};

/**
 * A data structure containing ChainSource objects keyed by network ID.
 */
export type LiquiditySources = {
  /** A ChainSources object. */
  [key: keyof typeof SUPPORTED_CHAINS_BY_CHAIN_ID]: ChainSources;
};

/**
 * Liquidity Sources Store.
 */
export type LiquiditySourcesStore = {
  addLiquiditySourcesForChain: (
    chainId: number,
    sources: SourcesList,
    total: number,
    selected: number,
  ) => void;
  hasAlteredLiquiditySources: () => boolean;
  hasAlteredLiquiditySourcesForChain: (chainId: number) => boolean;
  getExcludedSources: (chainId: number) => string;
  liquiditySources: LiquiditySources;
  resetLiquiditySources: () => void;
  toggleAllSourcesForChain: (chainId: number, shouldEnable: boolean) => void;
  setEnabledLiquiditySources: (chainId: number, sourceKeys: string[]) => void;
};

export const useLiquiditySourcesStore = createWithEqualityFn<LiquiditySourcesStore>()(
  persist(
    (set, get) => ({
      addLiquiditySourcesForChain: (
        chainId: number,
        sources: SourcesList,
        total: number,
        selected: number,
      ): void => {
        set((state) => ({
          liquiditySources: {
            ...state.liquiditySources,
            [chainId]: {
              selected,
              total,
              sources,
            },
          },
        }));
      },
      hasAlteredLiquiditySources: (): boolean => {
        const liquiditySources = get().liquiditySources;
        return !Object.keys(liquiditySources).every((key: string) => {
          const source = liquiditySources[parseInt(key, 10)];
          return source.selected !== source.total;
        });
      },
      hasAlteredLiquiditySourcesForChain: (chainId: number): boolean => {
        const liquiditySources = get().liquiditySources[chainId];
        return (
          liquiditySources !== undefined && liquiditySources.selected !== liquiditySources.total
        );
      },
      getExcludedSources: (chainId: number): string => {
        let excluded = '';
        const sources = get().liquiditySources?.[chainId]?.sources;

        if (sources)
          excluded = Object.keys(sources)
            .filter((key) => !sources[key].enabled)
            .join();

        return excluded;
      },
      liquiditySources: {},
      resetLiquiditySources: () => {
        // get all sources for all chains
        const liquiditySources = get().liquiditySources;

        // traverse the graph depth-first & set all sources to {enabled}
        for (const chainId in liquiditySources) {
          const chain = liquiditySources[Number(chainId)];

          for (const source in chain.sources) {
            chain.sources[source].enabled = true;
          }
        }
      },
      toggleAllSourcesForChain: (chainId: number, shouldEnable: boolean) => {
        // get all sources for the current chain
        const sources = get().liquiditySources[chainId].sources;

        // update {enabled} to reflect select/deselect all state
        for (const source in sources) {
          sources[source].enabled = shouldEnable;

          if (shouldEnable === false) {
            // at least one source is always required, so default to '0x' if it's available
            // otherwise use the first source from the alphabetized list
            const sourceToEnable = sources?.['0x']?.displayName ?? Object.keys(sources)[0];
            sources[sourceToEnable].enabled = true;
          }
        }

        set((state) => ({
          liquiditySources: {
            ...state.liquiditySources,
            [chainId]: {
              ...state.liquiditySources[chainId],
              selected: shouldEnable ? state.liquiditySources[chainId].total : 0,
              sources,
            },
          },
        }));
      },
      setEnabledLiquiditySources(chainId, sourceKeys) {
        // get all sources for the current chain
        const sources = get().liquiditySources[chainId].sources;
        const allSourceKeys = Object.keys(sources);

        const validatedSourceKeys = enforceRFQRules(sourceKeys, allSourceKeys);

        let enabled = 0;

        // update {enabled} to reflect select/deselect all state
        for (const source in sources) {
          if (validatedSourceKeys.includes(source)) {
            sources[source].enabled = true;
            enabled++;
          } else {
            sources[source].enabled = false;
          }
        }
        set((state) => ({
          liquiditySources: {
            ...state.liquiditySources,
            [chainId]: {
              ...state.liquiditySources[chainId],
              selected: enabled,
              sources,
            },
          },
        }));
      },
    }),
    {
      name: 'matcha-liquidity-sources-storage',
    },
  ),
  shallow,
);

/**
 * Selectors
 */
export const liquiditySourcesEventHandlers = ({
  liquiditySources,
  addLiquiditySourcesForChain,
  getExcludedSources,
  resetLiquiditySources,
  toggleAllSourcesForChain,
  setEnabledLiquiditySources,
}: LiquiditySourcesStore) => ({
  liquiditySources,
  addLiquiditySourcesForChain,
  getExcludedSources,
  resetLiquiditySources,
  toggleAllSourcesForChain,
  setEnabledLiquiditySources,
});

export const hasAlteredLiquiditySourcesForChainSelector =
  (chainId: number) => (state: LiquiditySourcesStore) =>
    state.hasAlteredLiquiditySourcesForChain(chainId);
